import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { LogOut } from 'react-feather';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SnackbarProvider } from './context/SnackbarContext';
import QuestionList from './components/QuestionList';
import CreateQuestion from './components/CreateQuestion';
import QuestionDetail from './components/QuestionDetail';
import Profile from './components/Profile';
import Login from './components/Login';
import Signup from './components/Signup';
import VerifyEmail from './components/VerifyEmail';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import InvitationPage from './components/InvitationPage';
import PublicQuestionPage from './components/PublicQuestionPage';
import OAuthCallback from './components/OAuthCallback';
import './App.css';

const PrivateRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const Header: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();

  return (
    <header className="app-header">
      <div className="header-content">
        <Link to="/" className="app-title">
          <span className="app-wordmark">Why<span className="app-wordmark-accent">Not</span>Log<span className="app-wordmark-accent">?</span></span>
        </Link>
        {isAuthenticated && (
          <div className="header-user">
            <Link to="/profile" className="user-email">{user?.name ?? user?.email}</Link>
            <button onClick={logout} className="btn btn-secondary btn-sm">
              <LogOut size={14} />
              Logout
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

function App() {
  return (
    <ThemeProvider>
      <SnackbarProvider>
        <AuthProvider>
          <Router>
            <div className="App">
              <Header />
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/invitations/:token" element={<InvitationPage />} />
                <Route path="/q/:slug" element={<PublicQuestionPage />} />
                <Route path="/auth/callback" element={<OAuthCallback />} />
                <Route
                  path="/profile"
                  element={
                    <PrivateRoute>
                      <Profile />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <QuestionList />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/questions/new"
                  element={
                    <PrivateRoute>
                      <CreateQuestion />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/questions/:id"
                  element={
                    <PrivateRoute>
                      <QuestionDetail />
                    </PrivateRoute>
                  }
                />
              </Routes>
            </div>
          </Router>
        </AuthProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
